import { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import rankingsService from '../services/rankingsService'
import availableStrategiesService from '../services/availableStrategiesService'
import LoadingSpinner from '../components/LoadingSpinner'
import ShareModal from '../components/ShareModal'
import EmbedCodeGenerator from '../components/EmbedCodeGenerator'
import ErrorBoundary from '../components/ErrorBoundary'
import { Avatar } from '../components/profile'
import LeaderboardTable from '../components/leaderboard/LeaderboardTable'
import { useFillViewportHeight } from '../hooks/useFillViewportHeight'
import {
  Trophy,
  Medal,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Share2,
  Code,
  UserPlus,
  LogIn,
  ExternalLink,
  Search,
  Filter,
  X,
  Gift,
  Calculator,
  Zap,
  BookOpen,
  BookMarked,
  CheckCircle2
} from 'lucide-react'
import { createLogger } from '../utils/logger'

const logger = createLogger('Rankings')

// Icon mapping for strategy types
const STRATEGY_ICONS = {
  SAT_MATH: Calculator,
  SAT_MATH_MINI: Zap,
  SAT_READING_WRITING: BookOpen,
  SAT_READING_WRITING_MINI: BookMarked,
  REFERRALS: Gift
}

// Special category constant for referrals
const REFERRALS_CATEGORY = 'REFERRALS'

/**
 * Rankings Page - Shows leaderboards and user rankings
 * Features:
 * - User's ranking summary across all challenge types (if logged in)
 * - Challenge type tabs
 * - Time period filters
 * - Paginated leaderboard table
 */
const Rankings = () => {
  const { user } = useAuthStore()
  const [searchParams] = useSearchParams()

  // URL params for shared links (e.g., /rankings?highlight=username&strategy=SAT_MATH_MINI)
  const highlightUser = searchParams.get('highlight')
  const urlStrategy = searchParams.get('strategy')

  // State
  const [selectedStrategy, setSelectedStrategy] = useState(urlStrategy || null)
  const [selectedPeriod, setSelectedPeriod] = useState('OVERALL_BEST')
  const [leaderboard, setLeaderboard] = useState({ entries: [], pagination: {} })
  const [userSummary, setUserSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [leaderboardLoading, setLeaderboardLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [showShareModal, setShowShareModal] = useState(false)
  const [showEmbedGenerator, setShowEmbedGenerator] = useState(false)
  const [strategyTypes, setStrategyTypes] = useState([])
  const [loadError, setLoadError] = useState(null)

  // Filter state
  const [showFilters, setShowFilters] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('') // Debounced value for API calls
  const [selectedLevel, setSelectedLevel] = useState(null) // null means all levels (minimum level filter)

  // User's position in the filtered leaderboard
  const [userPosition, setUserPosition] = useState(null)

  // Highlighted user info (for shared links)
  const [highlightedUserInfo, setHighlightedUserInfo] = useState(null)

  // Ref to track if initial strategy selection has been made
  const initialStrategySet = useRef(false)

  // Fill the viewport so the leaderboard card scrolls internally (banners/tabs/filters stay pinned
  // above, pagination pinned below) instead of leaving an empty band under a short board.
  const boardFillRef = useRef(null)
  const boardFillHeight = useFillViewportHeight(boardFillRef)

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300) // 300ms debounce

    return () => clearTimeout(timer)
  }, [searchTerm])

  const PAGE_SIZE = 15

  // Time periods for filters
  const timePeriods = [
    { code: 'OVERALL_BEST', name: 'All Time' },
    { code: 'TOP_OF_YEAR', name: 'This Year' },
    { code: 'TOP_OF_MONTH', name: 'This Month' },
    { code: 'TOP_OF_WEEK', name: 'This Week' }
  ]

  // Level options for filtering (1-10)
  const levelOptions = [
    { value: null, name: 'All Levels' },
    { value: 1, name: 'Level 1' },
    { value: 2, name: 'Level 2' },
    { value: 3, name: 'Level 3' },
    { value: 4, name: 'Level 4' },
    { value: 5, name: 'Level 5' },
    { value: 6, name: 'Level 6' },
    { value: 7, name: 'Level 7' },
    { value: 8, name: 'Level 8' },
    { value: 9, name: 'Level 9' },
    { value: 10, name: 'Level 10' }
  ]

  // Load available strategies and user summary on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoadError(null)
        // Load available strategies first
        const strategies = await availableStrategiesService.getAvailableStrategies()
        const formattedStrategies = strategies.map((s) => ({
          type: s.strategyType || s.type,
          name:
            s.name || availableStrategiesService.getStrategyDisplayName(s.strategyType || s.type)
        }))

        // Add Referrals as a special category at the end
        formattedStrategies.push({
          type: REFERRALS_CATEGORY,
          name: 'Referrals'
        })

        setStrategyTypes(formattedStrategies)

        // Load user summary if logged in
        let summary = null
        if (user) {
          summary = await rankingsService.getMyRankingSummary()
          setUserSummary(summary)
        }

        // Set initial selected strategy only once (avoid resetting on user change)
        // Priority: 1) URL param, 2) Best ranked strategy, 3) First available
        if (formattedStrategies.length > 0 && !initialStrategySet.current) {
          const validUrlStrategy =
            urlStrategy && formattedStrategies.some((s) => s.type === urlStrategy)

          if (validUrlStrategy) {
            setSelectedStrategy(urlStrategy)
          } else if (summary && Array.isArray(summary) && summary.length > 0) {
            // Find the strategy with the best (lowest) rank
            const rankedStrategies = summary.filter((s) => s.rank && s.rank > 0)
            if (rankedStrategies.length > 0) {
              const bestRanked = rankedStrategies.reduce((best, current) =>
                current.rank < best.rank ? current : best
              )
              // Make sure this strategy exists in our available strategies
              const strategyExists = formattedStrategies.some(
                (s) => s.type === bestRanked.strategyType
              )
              setSelectedStrategy(
                strategyExists ? bestRanked.strategyType : formattedStrategies[0].type
              )
            } else {
              setSelectedStrategy(formattedStrategies[0].type)
            }
          } else {
            setSelectedStrategy(formattedStrategies[0].type)
          }
          initialStrategySet.current = true
        }
      } catch (error) {
        logger.error('Error loading initial data:', error)
        setLoadError('Failed to load rankings data')
      } finally {
        setLoading(false)
      }
    }
    loadInitialData()
  }, [user, urlStrategy])

  // Load leaderboard when strategy, period, or filters change
  const loadLeaderboard = useCallback(async () => {
    if (!selectedStrategy) return
    setLeaderboardLoading(true)
    try {
      // Handle referrals category separately
      if (selectedStrategy === REFERRALS_CATEGORY) {
        const data = await rankingsService.getReferralLeaderboard({
          limit: PAGE_SIZE,
          offset: currentPage * PAGE_SIZE
        })
        setLeaderboard(data)
        setUserPosition(null) // Referrals don't have user position tracking
      } else {
        // Build filter options for level - exact level match when selected
        const levelFilters = selectedLevel
          ? { minLevel: selectedLevel, maxLevel: selectedLevel }
          : {}

        const data = await rankingsService.getLeaderboard(selectedStrategy, {
          period: selectedPeriod,
          limit: PAGE_SIZE,
          offset: currentPage * PAGE_SIZE,
          ...levelFilters,
          search: debouncedSearchTerm || undefined // Use debounced value
        })
        setLeaderboard(data)

        // Fetch user's position from the API if logged in
        if (user) {
          const position = await rankingsService.getMyPosition(
            selectedPeriod,
            selectedStrategy,
            levelFilters
          )
          if (position) {
            setUserPosition({
              rank: position.rank,
              total: position.total,
              level: selectedLevel,
              entry: position.entry
            })
          } else if (selectedLevel) {
            // User not found in this level's rankings
            setUserPosition({ notInLevel: true, level: selectedLevel })
          } else {
            // User has no rankings for this strategy
            setUserPosition(null)
          }
        } else {
          setUserPosition(null)
        }
      }
    } catch (error) {
      logger.error('Error loading leaderboard:', error)
    } finally {
      setLeaderboardLoading(false)
    }
  }, [selectedStrategy, selectedPeriod, currentPage, selectedLevel, debouncedSearchTerm, user])

  useEffect(() => {
    loadLeaderboard()
  }, [loadLeaderboard])

  // Load highlighted user data when URL contains a highlight param
  // This overrides normal leaderboard pagination to center on the highlighted user
  useEffect(() => {
    const loadHighlightedUser = async () => {
      if (!highlightUser || !selectedStrategy) return

      try {
        const data = await rankingsService.getUserPositionByName(
          highlightUser,
          selectedStrategy,
          selectedPeriod
        )

        if (data) {
          setHighlightedUserInfo({
            publicName: data.user.publicName,
            rank: data.user.rank,
            total: data.user.total
          })
          // Override leaderboard with centered entries
          setLeaderboard({
            entries: data.entries,
            pagination: data.pagination
          })
        } else {
          setHighlightedUserInfo(null)
        }
      } catch (error) {
        logger.error('Error loading highlighted user:', error)
        setHighlightedUserInfo(null)
      }
    }

    loadHighlightedUser()
  }, [highlightUser, selectedStrategy, selectedPeriod])

  // Handle tab change
  const handleStrategyChange = (strategy) => {
    setSelectedStrategy(strategy)
    setCurrentPage(0)
  }

  // Handle period change
  const handlePeriodChange = (period) => {
    setSelectedPeriod(period)
    setCurrentPage(0)
  }

  // Handle level filter change
  const handleLevelChange = (level) => {
    setSelectedLevel(level)
    setCurrentPage(0)
  }

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault()
    setCurrentPage(0)
    // The search term state change will trigger loadLeaderboard via useEffect
  }

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('')
    setSelectedLevel(null)
    setCurrentPage(0)
  }

  // Check if any filters are active
  const hasActiveFilters = searchTerm || selectedLevel

  // Get user's rank for current strategy from summary
  const getUserRankForStrategy = (strategyType) => {
    if (!userSummary || !Array.isArray(userSummary)) return null
    return userSummary.find((s) => s.strategyType === strategyType)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (loadError || strategyTypes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <Trophy className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4" />
        <p className="text-gray-500 dark:text-gray-400">
          {loadError || 'No challenge types available'}
        </p>
      </div>
    )
  }

  // Get user's ranking data for share modal
  const getShareRankingData = () => {
    const ranking = getUserRankForStrategy(selectedStrategy)
    return {
      publicName: user?.publicName,
      rank: ranking?.rank,
      totalParticipants: ranking?.totalParticipants,
      percentile: ranking?.percentile,
      score: ranking?.primaryScore,
      level: ranking?.level
    }
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Trophy className="w-8 h-8 text-yellow-500" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Rankings</h1>
        </div>
        <div className="flex items-center gap-2">
          {user?.isAdmin && (
            <button
              onClick={() => setShowEmbedGenerator(true)}
              className="btn-secondary flex items-center gap-2"
            >
              <Code className="w-4 h-4" />
              <span className="hidden sm:inline">Embed</span>
            </button>
          )}
          {user && (
            <button
              onClick={() => setShowShareModal(true)}
              className="btn-secondary flex items-center gap-2"
            >
              <Share2 className="w-4 h-4" />
              <span className="hidden sm:inline">Share</span>
            </button>
          )}
        </div>
      </div>

      {/* Featured User Banner (when viewing a shared link) */}
      {highlightedUserInfo && (
        <div className="card bg-gradient-to-r from-yellow-400 to-orange-500 text-white overflow-hidden">
          <div className="card-body py-4">
            <div className="flex items-center justify-center gap-3 text-center">
              <span className="text-2xl">⭐</span>
              <div>
                <p className="text-sm text-white/90">Viewing ranking for</p>
                <p className="text-xl font-bold">
                  {highlightedUserInfo.publicName} - #{highlightedUserInfo.rank} of{' '}
                  {highlightedUserInfo.total}
                </p>
              </div>
              <span className="text-2xl">⭐</span>
            </div>
          </div>
        </div>
      )}

      {/* CTA Banner for Anonymous Users */}
      {!user && (
        <div className="card bg-gradient-to-r from-primary-500 to-purple-600 text-white overflow-hidden">
          <div className="card-body py-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <Trophy className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold">Track Your Rankings</h3>
                  <p className="text-white/80 text-sm">
                    Create a free account to see where you rank among thousands of students
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Link
                  to="/login"
                  className="btn bg-white/20 hover:bg-white/30 text-white flex items-center gap-2"
                >
                  <LogIn className="w-4 h-4" />
                  Log In
                </Link>
                <Link
                  to="/signup"
                  className="btn bg-white text-primary-600 hover:bg-gray-100 flex items-center gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  Request Access
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Public Search Box - Always visible */}
      <div className="card">
        <div className="card-body py-4">
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="relative flex-1">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search for a player by username..."
                className="w-full px-4 py-3 pl-11 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
            </div>
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('')
                  setDebouncedSearchTerm('')
                }}
                className="btn-secondary px-4"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </form>
          {debouncedSearchTerm && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              Showing results for "{debouncedSearchTerm}"
            </p>
          )}
        </div>
      </div>

      {/* Category Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="flex overflow-x-auto scrollbar-hide">
          {strategyTypes.map((strategy) => {
            const ranking = user ? getUserRankForStrategy(strategy.type) : null
            const isSelected = selectedStrategy === strategy.type
            const hasResults = ranking?.rank && ranking.rank > 0
            const IconComponent = STRATEGY_ICONS[strategy.type] || Trophy

            return (
              <button
                key={strategy.type}
                onClick={() => handleStrategyChange(strategy.type)}
                className={`flex-shrink-0 flex items-center gap-3 px-5 py-4 border-b-2 transition-all ${
                  isSelected
                    ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-900/20'
                    : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <div className="relative">
                  <IconComponent
                    className={`w-5 h-5 ${
                      isSelected
                        ? 'text-primary-600 dark:text-primary-400'
                        : hasResults
                          ? 'text-green-500 dark:text-green-400'
                          : 'text-gray-400 dark:text-gray-500'
                    }`}
                  />
                  {/* Green checkmark badge for strategies with results */}
                  {hasResults && !isSelected && (
                    <CheckCircle2 className="absolute -top-1 -right-1 w-3 h-3 text-green-500 dark:text-green-400 bg-white dark:bg-gray-800 rounded-full" />
                  )}
                </div>
                <div className="text-left">
                  <p
                    className={`text-sm font-medium ${
                      isSelected
                        ? 'text-primary-700 dark:text-primary-300'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {strategy.name}
                  </p>
                  {user && ranking?.rank ? (
                    <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                      #{ranking.rank} of {ranking.totalParticipants?.toLocaleString() || '?'}
                    </p>
                  ) : user ? (
                    <p className="text-xs text-gray-400 dark:text-gray-500">Not ranked</p>
                  ) : null}
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Filters Section - Hide for Referrals */}
      {selectedStrategy !== REFERRALS_CATEGORY && (
        <div className="space-y-4">
          {/* Time Period and Level Tabs Row */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            {/* Time Period Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                Time:
              </span>
              <div className="flex gap-1 flex-wrap">
                {timePeriods.map((period) => (
                  <button
                    key={period.code}
                    onClick={() => handlePeriodChange(period.code)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      selectedPeriod === period.code
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {period.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Level Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                Level:
              </span>
              <select
                value={selectedLevel || ''}
                onChange={(e) =>
                  handleLevelChange(e.target.value ? parseInt(e.target.value, 10) : null)
                }
                className="px-3 py-1.5 rounded-md text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-0 focus:ring-2 focus:ring-primary-500"
              >
                {levelOptions.map((option) => (
                  <option key={option.value || 'all'} value={option.value || ''}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Toggle Advanced Filters Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                showFilters || hasActiveFilters
                  ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              <Filter className="w-4 h-4" />
              <span>Filters</span>
              {hasActiveFilters && <span className="w-2 h-2 bg-primary-500 rounded-full" />}
            </button>
          </div>

          {/* Advanced Filters (collapsible) */}
          {showFilters && (
            <div className="card">
              <div className="card-body py-4">
                <div className="flex flex-wrap items-end gap-4">
                  {/* Search */}
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Search User
                    </label>
                    <form onSubmit={handleSearch} className="relative">
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search by username..."
                        className="w-full px-3 py-2 pl-9 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                      <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    </form>
                  </div>

                  {/* Clear Filters */}
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                    >
                      <X className="w-4 h-4" />
                      Clear Filters
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Leaderboard Table — filled to the viewport so the board scrolls internally while the
          header stays pinned above and pagination stays pinned below. */}
      <div
        ref={boardFillRef}
        className="card flex flex-col"
        style={boardFillHeight ? { height: boardFillHeight } : undefined}
      >
        <div className="card-header shrink-0">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Medal className="w-5 h-5 text-yellow-500" />
              {rankingsService.getStrategyDisplayName(selectedStrategy)} Leaderboard
            </h3>
            <div className="flex items-center gap-3">
              {/* User Position Display */}
              {user && userPosition && (
                <>
                  <span className="text-gray-300 dark:text-gray-600">|</span>
                  <div className="text-sm">
                    {userPosition.notInLevel ? (
                      <span className="text-gray-500 dark:text-gray-400 italic">
                        Not ranked at Level {userPosition.level}
                      </span>
                    ) : (
                      <span className="text-primary-600 dark:text-primary-400 font-medium">
                        Ranked {userPosition.rank} of {userPosition.total}
                        {userPosition.level && ` at Level ${userPosition.level}`}
                      </span>
                    )}
                  </div>
                </>
              )}
              {/* Total Participants */}
              {leaderboard.pagination?.total > 0 && (
                <>
                  <span className="text-gray-300 dark:text-gray-600">|</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {leaderboard.pagination.total.toLocaleString()}{' '}
                    {selectedStrategy === REFERRALS_CATEGORY ? 'referrers' : 'participants'}
                  </span>
                </>
              )}
              {/* Time Period - hide for referrals */}
              {selectedStrategy !== REFERRALS_CATEGORY && (
                <>
                  <span className="text-gray-300 dark:text-gray-600">|</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {rankingsService.getTimePeriodDisplayName(selectedPeriod)}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="card-body p-0 flex-1 min-h-0 flex flex-col">
          {leaderboardLoading ? (
            <div className="flex items-center justify-center py-12 flex-1">
              <LoadingSpinner size="md" />
            </div>
          ) : leaderboard.entries?.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
              {selectedStrategy === REFERRALS_CATEGORY ? (
                <>
                  <Gift className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">No referrals yet</p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                    Be the first to invite friends!
                  </p>
                </>
              ) : (
                <>
                  <Trophy className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">No rankings yet</p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                    Be the first to complete a challenge!
                  </p>
                </>
              )}
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-auto min-h-0">
                {selectedStrategy === REFERRALS_CATEGORY ? (
                  <ReferralLeaderboardTable
                    entries={leaderboard.entries}
                    currentUserId={user?.id}
                    startRank={currentPage * PAGE_SIZE + 1}
                  />
                ) : (
                  <LeaderboardTable
                    entries={leaderboard.entries}
                    currentUserId={user?.id}
                    startRank={
                      highlightUser
                        ? leaderboard.pagination?.offset + 1
                        : currentPage * PAGE_SIZE + 1
                    }
                    highlightedPublicName={highlightUser}
                    userPosition={userPosition}
                    topCount={15}
                  />
                )}
              </div>
              {/* Pagination */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Showing {currentPage * PAGE_SIZE + 1} -{' '}
                  {Math.min(
                    (currentPage + 1) * PAGE_SIZE,
                    currentPage * PAGE_SIZE + leaderboard.entries.length
                  )}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                    disabled={currentPage === 0}
                    className="btn-secondary p-2 disabled:opacity-50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => p + 1)}
                    disabled={!leaderboard.pagination?.hasMore}
                    className="btn-secondary p-2 disabled:opacity-50"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        rankingData={getShareRankingData()}
        strategyType={selectedStrategy}
      />

      {/* Embed Code Generator */}
      <EmbedCodeGenerator
        isOpen={showEmbedGenerator}
        onClose={() => setShowEmbedGenerator(false)}
      />

      {/* Deaveris Branding Footer for Unauthenticated Users */}
      {!user && (
        <div className="text-center py-8 border-t border-gray-200 dark:border-gray-700 mt-8">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Powered by</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            <span className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent">
              Deaveris
            </span>
          </Link>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            SAT prep with adaptive learning
          </p>
          <div className="mt-4 flex items-center justify-center gap-4">
            <Link
              to="/signup"
              className="text-sm text-primary-600 dark:text-primary-400 hover:underline font-medium"
            >
              Start practicing for free →
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Referral Leaderboard Table - Displays top referrers
 */
const ReferralLeaderboardTable = ({ entries, currentUserId, startRank }) => {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <th className="text-left py-3 px-6 text-sm font-medium text-gray-600 dark:text-gray-400 w-20">
              Rank
            </th>
            <th className="text-left py-3 px-6 text-sm font-medium text-gray-600 dark:text-gray-400">
              User
            </th>
            <th className="text-left py-3 px-6 text-sm font-medium text-gray-600 dark:text-gray-400 w-40">
              Successful Referrals
            </th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry, index) => {
            const rank = entry.rank || startRank + index
            const isCurrentUser = entry.userId === currentUserId
            const rankInfo = rankingsService.formatRank(rank)

            return (
              <tr
                key={entry.userId || index}
                className={`border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 ${
                  isCurrentUser
                    ? 'bg-yellow-50 dark:bg-yellow-900/20 ring-2 ring-yellow-400 dark:ring-yellow-500'
                    : ''
                }`}
              >
                <td className="py-4 px-6">
                  <div className="flex items-center">
                    {rankInfo.isMedal ? (
                      <span className="text-2xl">{rankInfo.display}</span>
                    ) : (
                      <span className={`font-semibold ${rankInfo.color} dark:text-gray-400`}>
                        {rankInfo.display}
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-4 px-6">
                  <div className="flex items-center gap-3">
                    <Avatar
                      type={entry.avatarType || 'dicebear'}
                      seed={entry.avatarSeed}
                      style={entry.avatarStyle || 'bottts'}
                      data={entry.avatarData}
                      publicName={entry.publicName}
                      size="sm"
                    />
                    <div>
                      {entry.publicName ? (
                        <Link
                          to={`/u/${entry.publicName}`}
                          className="font-medium text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                        >
                          {entry.publicName}
                        </Link>
                      ) : (
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          Anonymous
                        </span>
                      )}
                      {isCurrentUser && (
                        <span className="ml-2 text-xs text-primary-600 dark:text-primary-400 font-medium">
                          (You)
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="py-4 px-6">
                  <div className="flex items-center gap-2">
                    <UserPlus className="w-4 h-4 text-green-500" />
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      {entry.successfulReferrals}
                    </span>
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// Wrap with ErrorBoundary for graceful error handling
const RankingsWithErrorBoundary = () => (
  <ErrorBoundary>
    <Rankings />
  </ErrorBoundary>
)

export default RankingsWithErrorBoundary
